
import { useState } from 'react'
import { supabase } from '../../../lib/supabase'
import { useAuth } from '../../../hooks/useAuth'

interface NewMessageModalProps {
  isOpen: boolean
  onClose: () => void
  onStartConversation: (userId: string) => Promise<void>
}

interface UserResult {
  id: string
  username: string
  avatar_url: string | null
}

const NewMessageModal = ({ isOpen, onClose, onStartConversation }: NewMessageModalProps) => {
  const { user } = useAuth()
  const [searchQuery, setSearchQuery] = useState('')
  const [results, setResults] = useState<UserResult[]>([])
  const [searching, setSearching] = useState(false)
  const [starting, setStarting] = useState<string | null>(null)

  const handleSearch = async (query: string) => {
    setSearchQuery(query)
    if (query.trim().length < 2) {
      setResults([])
      return
    }

    setSearching(true)
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, username, avatar_url')
        .neq('id', user?.id || '')
        .ilike('username', `%${query.trim()}%`)
        .limit(10)

      if (!error && data) {
        setResults(data)
      }
    } catch (err) {
      console.error('Search error:', err)
    } finally {
      setSearching(false)
    }
  }

  const handleSelect = async (userId: string) => {
    setStarting(userId)
    try {
      await onStartConversation(userId)
      setSearchQuery('')
      setResults([])
      onClose()
    } catch (err) {
      console.error('Error starting conversation:', err)
    } finally {
      setStarting(null)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-base font-bold text-gray-900">New Message</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg text-gray-500"></i>
          </button>
        </div>

        <div className="p-4">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search by username..."
              className="w-full pl-9 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
              autoFocus
            />
          </div>
        </div>

        <div className="max-h-72 overflow-y-auto px-2 pb-4">
          {searching ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500"></div>
            </div>
          ) : results.length > 0 ? (
            results.map((u) => (
              <button
                key={u.id}
                onClick={() => handleSelect(u.id)}
                disabled={starting === u.id}
                className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl hover:bg-purple-50 transition-colors cursor-pointer disabled:opacity-50"
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center text-white font-semibold text-sm overflow-hidden flex-shrink-0">
                  {u.avatar_url ? (
                    <img src={u.avatar_url} alt="" className="w-full h-full object-cover object-top" />
                  ) : (
                    u.username?.[0]?.toUpperCase() || 'U'
                  )}
                </div>
                <span className="text-sm font-medium text-gray-800">{u.username}</span>
                {starting === u.id && (
                  <i className="ri-loader-4-line animate-spin text-purple-500 ml-auto"></i>
                )}
              </button>
            ))
          ) : searchQuery.trim().length >= 2 ? (
            <div className="text-center py-8">
              <i className="ri-user-search-line text-3xl text-gray-300 block mb-2"></i>
              <p className="text-sm text-gray-400">No users found</p>
            </div>
          ) : (
            <div className="text-center py-8">
              <i className="ri-user-search-line text-3xl text-gray-300 block mb-2"></i>
              <p className="text-sm text-gray-400">Type at least 2 characters to search</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default NewMessageModal
